/**
 * MyActionsTray — top-nav dropdown showing every open MIR / MRN / Discrepancy
 * where the logged-in user is requester, approver, assignee or raiser.
 * One click reveals the tray; each row deep-links to the item's tab.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Inbox, ClipboardList, PackageX, AlertTriangle, Loader2, ChevronRight } from 'lucide-react';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444' };

export default function MyActionsTray() {
  const { projectId } = useParams();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/mtx/my/open-actions`);
      setData(data);
    } catch { setData(null); }
    setLoading(false);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Auto-refresh every 60s while mounted
    const t = setInterval(load, 60000); return () => clearInterval(t);
  }, [load]);

  if (!projectId) return null;
  const total = data?.total || 0;

  const goTab = (tab, id) => {
    setOpen(false);
    nav(`/project-controls/material-tracking?tab=${tab}&open=${id}`);
  };

  return (
    <div className="relative" data-testid="my-actions-tray">
      <button onClick={() => setOpen(v => !v)}
        data-testid="my-actions-toggle"
        className="relative flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100"
        title="My Open Actions">
        <Inbox className="w-4 h-4" style={{ color: C.slate }} />
        <span className="text-[11px] font-semibold" style={{ color: C.slate }}>Actions</span>
        {total > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold text-white flex items-center justify-center"
            style={{ background: C.accent }} data-testid="my-actions-count">{total}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-8 w-96 bg-white rounded-lg shadow-2xl border overflow-hidden z-50"
          style={{ borderColor: C.border }} data-testid="my-actions-panel">
          <div className="p-2 border-b flex items-center gap-2" style={{ borderColor: C.border, background: '#F8FAFC' }}>
            <Inbox className="w-4 h-4" style={{ color: C.primary }} />
            <div className="text-[11px] font-bold" style={{ color: C.slate }}>My Open Actions</div>
            <span className="ml-auto text-[10px]" style={{ color: C.slateMuted }}>{total} open</span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading && <div className="p-3 text-center"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>}
            {!loading && total === 0 && (
              <div className="p-4 text-center text-[10px]" style={{ color: C.slateMuted }}>
                All caught up · nothing waiting on you
              </div>
            )}
            {!loading && (data?.mirs || []).length > 0 && (
              <div>
                <div className="px-2 py-1 text-[9px] uppercase font-semibold" style={{ background: '#EEF2FF', color: C.primary }}>
                  MIR · Material Issue ({data.mirs.length})
                </div>
                {data.mirs.slice(0, 20).map(m => (
                  <button key={m.id} onClick={() => goTab('field-issue', m.id)}
                    data-testid={`my-mir-${m.code}`}
                    className="w-full text-left px-2 py-1.5 hover:bg-slate-50 flex items-center gap-2"
                    style={{ borderBottom: `1px solid ${C.border}` }}>
                    <ClipboardList className="w-3 h-3 flex-shrink-0" style={{ color: C.primary }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-mono font-bold truncate" style={{ color: C.slate }}>{m.code}</div>
                      <div className="text-[9px] truncate" style={{ color: C.slateMuted }}>
                        {m.warehouseLocation} · {m.workfront || 'no workfront'} · <b>{m.status}</b>
                      </div>
                    </div>
                    <ChevronRight className="w-3 h-3" style={{ color: C.slateMuted }} />
                  </button>
                ))}
              </div>
            )}
            {!loading && (data?.mrns || []).length > 0 && (
              <div>
                <div className="px-2 py-1 text-[9px] uppercase font-semibold" style={{ background: '#FEF3C7', color: '#92400E' }}>
                  MRN · Field Return ({data.mrns.length})
                </div>
                {data.mrns.slice(0, 20).map(m => (
                  <button key={m.id} onClick={() => goTab('field-return', m.id)}
                    data-testid={`my-mrn-${m.code}`}
                    className="w-full text-left px-2 py-1.5 hover:bg-slate-50 flex items-center gap-2"
                    style={{ borderBottom: `1px solid ${C.border}` }}>
                    <PackageX className="w-3 h-3 flex-shrink-0" style={{ color: C.accent }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-mono font-bold truncate" style={{ color: C.slate }}>{m.code}</div>
                      <div className="text-[9px] truncate" style={{ color: C.slateMuted }}>
                        {m.warehouseLocation} · {m.returnType?.replace('_', ' ')} · <b>{m.status}</b>
                      </div>
                    </div>
                    <ChevronRight className="w-3 h-3" style={{ color: C.slateMuted }} />
                  </button>
                ))}
              </div>
            )}
            {!loading && (data?.discrepancies || []).length > 0 && (
              <div>
                <div className="px-2 py-1 text-[9px] uppercase font-semibold" style={{ background: '#FEE2E2', color: C.danger }}>
                  Discrepancies ({data.discrepancies.length})
                </div>
                {data.discrepancies.slice(0, 20).map(d => (
                  <button key={d.id} onClick={() => goTab('reconcile', d.id)}
                    data-testid={`my-disc-${d.id}`}
                    className="w-full text-left px-2 py-1.5 hover:bg-slate-50 flex items-center gap-2"
                    style={{ borderBottom: `1px solid ${C.border}` }}>
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" style={{ color: C.danger }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-mono font-bold truncate" style={{ color: C.slate }}>{d.id}</div>
                      <div className="text-[9px] truncate" style={{ color: C.slateMuted }}>
                        {d.warehouseLocation} · Var {d.variance} · ${d.valueImpact || 0} · <b>{d.category || 'uncategorised'}</b>
                      </div>
                    </div>
                    <ChevronRight className="w-3 h-3" style={{ color: C.slateMuted }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
